import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  creditsService, sitesService, suppliersService, beverageProductsService,
  stocktakingSettingsService, stocktakeAttachmentService, supplierProductsService,
} from '../services';
import { useSettings } from '../contexts/SettingsContext';
import ProductPicker from '../components/ProductPicker';
import SitePicker from '../components/SitePicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import {
  Save, Loader2, Trash2, Plus, ArrowLeft, CheckCircle2, Undo2,
  RotateCcw, AlertTriangle, Paperclip, Upload, X, FileDown
} from 'lucide-react';
import api from '../services/api';
import { getApiError } from '@/lib/utils';
import { getYieldHint } from '@/lib/productHelpers';

const emptyLine = () => ({
  beverage_product_id: '', quantity: '', cost_price: '', unit_label: '', notes: '',
});

export default function CreditEntryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currencySymbol } = useSettings();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [saveAndPosting, setSaveAndPosting] = useState(false);
  const [credit, setCredit] = useState(null);

  const [header, setHeader] = useState({
    site_id: '', supplier_id: '', reference_number: '',
    credit_date: new Date().toISOString().split('T')[0],
    notes: '', credit_total: '',
  });
  const [lines, setLines] = useState([emptyLine()]);

  const [sites, setSites] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);

  const [settings, setSettings] = useState({ default_vat_rate: 20, invoice_basis: 'net' });
  const [supplierProducts, setSupplierProducts] = useState(null);
  const [uploading, setUploading] = useState(false);

  const isPosted = credit?.status === 'posted';
  // Posted credits remain editable post-posting (matches Delivery flow) —
  // users can correct references, quantities, costs and delete lines while
  // the session is still open. Unpost/Delete actions are surfaced below.
  const editDisabled = false;

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [sitesData, suppliersData, productsData, settingsData] = await Promise.all([
          sitesService.list({ limit: 500 }),
          suppliersService.list({ limit: 500, is_active: true }),
          beverageProductsService.listAll({ status: 'active' }),
          stocktakingSettingsService.get().catch(() => ({ default_vat_rate: 20, invoice_basis: 'net' })),
        ]);
        setSites(Array.isArray(sitesData) ? sitesData : sitesData.sites || sitesData.items || []);
        setSuppliers(suppliersData.items || []);
        setProducts(Array.isArray(productsData) ? productsData : productsData.items || []);
        setSettings(settingsData);
      } catch { /* silent */ }
    };
    loadLookups();
  }, []);

  useEffect(() => {
    if (isNew) return;
    const loadCredit = async () => {
      try {
        const c = await creditsService.get(Number(id));
        setCredit(c);
        setHeader({
          site_id: String(c.site_id || ''),
          supplier_id: String(c.supplier_id || ''),
          reference_number: c.reference_number || '',
          credit_date: c.credit_date || '',
          notes: c.notes || '',
          credit_total: c.credit_total !== null && c.credit_total !== undefined ? String(c.credit_total) : '',
        });
        if (c.lines?.length > 0) {
          setLines(c.lines.map(ln => ({
            beverage_product_id: String(ln.beverage_product_id),
            quantity: String(ln.quantity),
            cost_price: String(ln.cost_price),
            unit_label: ln.unit_label || '',
            notes: ln.notes || '',
            product_name: ln.product_name || '',
          })));
        }
      } catch {
        toast({ title: 'Error', description: 'Failed to load credit', variant: 'destructive' });
        navigate('/stocktaking/credits');
      } finally {
        setLoading(false);
      }
    };
    loadCredit();
  }, [id, isNew, navigate, toast]);

  useEffect(() => {
    if (!header.supplier_id) { setSupplierProducts(null); return; }
    supplierProductsService.getLinkedProducts(header.supplier_id)
      .then(data => setSupplierProducts(data.products?.length > 0 ? data.products : null))
      .catch(() => setSupplierProducts(null));
  }, [header.supplier_id]);

  const availableProducts = useMemo(() => {
    if (!supplierProducts) return products;
    const linkedIds = new Set(supplierProducts.map(p => p.beverage_product_id));
    return products.filter(p => linkedIds.has(p.id));
  }, [products, supplierProducts]);

  const getProduct = (productId) => {
    if (!productId) return null;
    return products.find(pr => String(pr.id) === String(productId)) || null;
  };

  const getUnitOptions = (productId) => {
    const prod = getProduct(productId);
    if (!prod || !prod.units?.length) return [];
    const options = prod.units.map(u => {
      let label = u.unit_type.charAt(0).toUpperCase() + u.unit_type.slice(1);
      if (u.conversion_to_base && u.unit_type !== 'case') label += ` ${u.conversion_to_base}ml`;
      return { value: u.unit_type, label };
    });
    if (prod.case_size && prod.case_size > 0 && !prod.units.some(u => u.unit_type === 'case')) {
      options.push({ value: 'case', label: `Case (${prod.case_size})` });
    }
    const caseIdx = options.findIndex(o => o.value === 'case');
    if (caseIdx >= 0 && prod.case_size) options[caseIdx].label = `Case (${prod.case_size})`;
    return options;
  };

  const getConversionNote = (ln) => {
    const prod = getProduct(ln.beverage_product_id);
    if (!prod || !prod.case_size || prod.case_size <= 0 || ln.unit_label !== 'case') return null;
    const qty = parseFloat(ln.quantity) || 0;
    if (qty <= 0) return null;
    const totalSingles = Math.round(qty * prod.case_size * 100) / 100;
    const fullCases = Math.floor(qty);
    const remainder = Math.round((qty - fullCases) * prod.case_size);
    const singleUnit = prod.units?.find(u => u.unit_type !== 'case')?.unit_type || 'unit';
    const singleLabel = singleUnit.charAt(0).toUpperCase() + singleUnit.slice(1) + (totalSingles !== 1 ? 's' : '');
    if (remainder > 0) return `= ${totalSingles} ${singleLabel} (${fullCases} case${fullCases !== 1 ? 's' : ''} + ${remainder} ${singleUnit}${remainder !== 1 ? 's' : ''})`;
    return `= ${totalSingles} ${singleLabel}`;
  };

  const handleProductChange = (idx, productId) => {
    const updates = { beverage_product_id: productId };
    if (productId) {
      if (supplierProducts) {
        const link = supplierProducts.find(sp => String(sp.beverage_product_id) === String(productId));
        if (link?.supplier_cost_price) updates.cost_price = String(link.supplier_cost_price);
      }
      const prod = getProduct(productId);
      const units = prod?.units || [];
      const purchaseUnit = units.find(u => u.is_default_purchase_unit) || units.find(u => u.is_base_stock_unit) || units[0];
      if (purchaseUnit?.unit_type) updates.unit_label = purchaseUnit.unit_type;
    }
    setLines(prev => prev.map((ln, i) => i === idx ? { ...ln, ...updates } : ln));
  };

  const linesTotal = useMemo(() => lines.reduce((sum, ln) => {
    return sum + (parseFloat(ln.quantity) || 0) * (parseFloat(ln.cost_price) || 0);
  }, 0), [lines]);

  const creditTotal = parseFloat(header.credit_total) || 0;
  const vatRate = (settings.default_vat_rate || 0) / 100;
  const invoiceBasis = settings.invoice_basis || 'net';

  const vatBreakdown = useMemo(() => {
    if (invoiceBasis === 'gross') {
      const gross = linesTotal;
      const net = gross / (1 + vatRate);
      return { net, vat: gross - net, gross };
    }
    const net = linesTotal;
    const vat = net * vatRate;
    return { net, vat, gross: net + vat };
  }, [linesTotal, vatRate, invoiceBasis]);

  const variance = header.credit_total ? creditTotal - vatBreakdown.gross : 0;

  const updateLine = (idx, field, value) => {
    setLines(prev => prev.map((ln, i) => i === idx ? { ...ln, [field]: value } : ln));
  };
  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => {
    if (lines.length <= 1) return;
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  const getProductName = (prodId) => {
    const p = products.find(pr => String(pr.id) === String(prodId));
    return p ? `${p.product_code ? p.product_code + ' - ' : ''}${p.product_name}` : '';
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !credit?.id) return;
    setUploading(true);
    try {
      const result = await stocktakeAttachmentService.uploadCredit(credit.id, file);
      setCredit(prev => ({ ...prev, attachment_url: result.attachment_url, attachment_filename: result.attachment_filename }));
      toast({ title: 'Uploaded', description: 'Attachment uploaded successfully' });
    } catch {
      toast({ title: 'Error', description: 'Failed to upload attachment', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const handleAttachmentDownload = async () => {
    if (!credit?.id) return;
    try {
      const response = await stocktakeAttachmentService.downloadCreditAttachment(credit.id);
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toast({ title: 'Error', description: 'Failed to download attachment', variant: 'destructive' });
    }
  };

  const handleAttachmentRemove = async () => {
    if (!credit?.id) return;
    try {
      await stocktakeAttachmentService.removeCredit(credit.id);
      setCredit(prev => ({ ...prev, attachment_url: null, attachment_filename: null }));
      toast({ title: 'Removed', description: 'Attachment removed' });
    } catch {
      toast({ title: 'Error', description: 'Failed to remove attachment', variant: 'destructive' });
    }
  };

  // Save — returns creditId for chaining
  const handleSave = async (showToast = true) => {
    if (!header.site_id) { toast({ title: 'Validation', description: 'Please select a site', variant: 'destructive' }); return null; }
    if (!header.credit_date) { toast({ title: 'Validation', description: 'Please enter a credit date', variant: 'destructive' }); return null; }
    const validLines = lines.filter(ln => ln.beverage_product_id && parseFloat(ln.quantity));

    setSaving(true);
    try {
      const headerPayload = {
        site_id: Number(header.site_id),
        supplier_id: header.supplier_id ? Number(header.supplier_id) : null,
        reference_number: header.reference_number || null,
        credit_date: header.credit_date,
        notes: header.notes || null,
        credit_total: header.credit_total ? parseFloat(header.credit_total) : null,
      };

      let creditId;
      if (isNew) {
        const created = await creditsService.create(headerPayload);
        creditId = created.id;
      } else {
        await creditsService.update(Number(id), headerPayload);
        creditId = Number(id);
      }

      if (validLines.length > 0) {
        // Bug fix (2026-04-20, Issue 2): send `unit_id` whenever we can
        // resolve it from the product's units list. Sending only a
        // `unit_label` makes the backend fall back to a string match that
        // silently fails when the stored label drifts.
        const linePayloads = validLines.map(ln => {
          const prod = products.find(p => p.id === Number(ln.beverage_product_id));
          const resolvedUnit = prod?.units?.find(
            u => (u.unit_type || '').trim().toLowerCase() === (ln.unit_label || '').trim().toLowerCase()
          );
          return {
            beverage_product_id: Number(ln.beverage_product_id),
            quantity: parseFloat(ln.quantity),
            cost_price: parseFloat(ln.cost_price) || 0,
            unit_label: ln.unit_label || null,
            unit_id: resolvedUnit ? resolvedUnit.id : null,
            notes: ln.notes || null,
          };
        });
        await api.put(`/stocktake/credits/${creditId}/lines`, { lines: linePayloads });
      }

      const updated = await creditsService.get(creditId);
      setCredit(updated);
      if (updated.lines?.length > 0) {
        setLines(updated.lines.map(ln => ({
          beverage_product_id: String(ln.beverage_product_id),
          quantity: String(ln.quantity),
          cost_price: String(ln.cost_price),
          unit_label: ln.unit_label || '',
          notes: ln.notes || '',
        })));
      }

      if (showToast) toast({ title: 'Saved', description: 'Credit saved successfully' });
      if (isNew) navigate(`/stocktaking/credits/${creditId}`, { replace: true });
      return creditId;
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save'), variant: 'destructive' });
      return null;
    } finally { setSaving(false); }
  };

  const handlePost = async () => {
    if (!credit?.id) return;
    if (!window.confirm('Post this credit note?')) return;
    setPosting(true);
    try {
      const updated = await creditsService.post(credit.id);
      setCredit(updated);
      toast({ title: 'Posted', description: 'Credit note posted.' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to post'), variant: 'destructive' });
    } finally { setPosting(false); }
  };

  const handleSaveAndPost = async () => {
    if (!window.confirm('Save and post this credit note?')) return;
    setSaveAndPosting(true);
    try {
      const savedId = await handleSave(false);
      if (!savedId) { setSaveAndPosting(false); return; }
      const updated = await creditsService.post(savedId);
      setCredit(updated);
      toast({ title: 'Saved & Posted', description: 'Credit saved and posted successfully' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save & post'), variant: 'destructive' });
    } finally { setSaveAndPosting(false); }
  };

  const handleUnpost = async () => {
    if (!credit?.id) return;
    if (!window.confirm('Unpost this credit? It will return to Draft status.')) return;
    setPosting(true);
    try {
      const updated = await creditsService.unpost(credit.id);
      setCredit(updated);
      toast({ title: 'Unposted', description: 'Credit returned to Draft' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to unpost'), variant: 'destructive' });
    } finally { setPosting(false); }
  };

  const handleDelete = async () => {
    if (!credit?.id) return;
    const msg = isPosted
      ? 'Delete this posted credit? This will remove it from the session entirely and cannot be undone.'
      : 'Delete this credit? This cannot be undone.';
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      await creditsService.delete(credit.id);
      toast({ title: 'Credit deleted' });
      navigate('/stocktaking/credits');
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete'), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  const vatLabel = invoiceBasis === 'gross' ? 'incl. VAT' : 'excl. VAT';
  const isBusy = saving || posting || saveAndPosting;

  return (
    <div className="space-y-6" data-testid="credit-entry-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/credits')} data-testid="back-to-credit-register">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <RotateCcw className="h-6 w-6 text-slate-600" />
              {isNew ? 'New Credit' : `Credit #${id}`}
            </h1>
            {credit && (
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={isPosted ? 'default' : 'secondary'} className={isPosted ? 'bg-emerald-100 text-emerald-800' : ''}>
                  {isPosted ? 'Posted' : 'Draft'}
                </Badge>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Save is always available (both posted and draft) so users can
              correct posted credits without unposting first. */}
          <Button onClick={() => handleSave(true)} disabled={isBusy} variant="outline" data-testid="save-credit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {isPosted ? 'Save changes' : 'Save'}
          </Button>
          {isPosted && (
            <Button onClick={handleUnpost} disabled={isBusy} variant="outline" data-testid="unpost-credit-btn">
              {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Undo2 className="h-4 w-4 mr-2" />}
              Unpost
            </Button>
          )}
          {!isPosted && (
            <Button onClick={handleSaveAndPost} disabled={isBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-post-credit-btn">
              {saveAndPosting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Save & Post
            </Button>
          )}
          {/* Delete is always available while the session is open. */}
          {credit?.id && (
            <Button onClick={handleDelete} disabled={isBusy} variant="outline"
              className="text-destructive border-destructive/30 hover:bg-destructive/10"
              data-testid="delete-credit-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Credit Details</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Site *</Label>
              <SitePicker sites={sites} value={header.site_id} onChange={(v) => setHeader(h => ({ ...h, site_id: v }))} disabled={editDisabled} testId="credit-site-select" />
            </div>
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Select value={header.supplier_id || 'none'} onValueChange={(v) => setHeader(h => ({ ...h, supplier_id: v === 'none' ? '' : v }))} disabled={editDisabled}>
                <SelectTrigger data-testid="credit-supplier-select"><SelectValue placeholder="Select supplier..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No supplier --</SelectItem>
                  {suppliers.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.supplier_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {supplierProducts && <p className="text-xs text-blue-600">Showing {availableProducts.length} products linked to this supplier</p>}
            </div>
            <div className="space-y-2">
              <Label>Credit Date *</Label>
              <Input type="date" value={header.credit_date} onChange={(e) => setHeader(h => ({ ...h, credit_date: e.target.value }))} disabled={editDisabled} data-testid="credit-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Reference / Credit Note No.</Label>
              <Input value={header.reference_number} onChange={(e) => setHeader(h => ({ ...h, reference_number: e.target.value }))} placeholder="e.g. CN-12345" disabled={editDisabled} data-testid="credit-reference-input" />
            </div>
            <div className="space-y-2">
              <Label>Credit Total ({currencySymbol}) <span className="text-xs text-muted-foreground ml-1">incl. VAT</span></Label>
              <Input type="number" step="0.01" value={header.credit_total} onChange={(e) => setHeader(h => ({ ...h, credit_total: e.target.value }))} placeholder="Optional" disabled={editDisabled} data-testid="credit-total-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={header.notes} onChange={(e) => setHeader(h => ({ ...h, notes: e.target.value }))} placeholder="Optional notes" disabled={editDisabled} data-testid="credit-notes-input" />
            </div>
          </div>
          {!isNew && (
            <div className="mt-4 pt-4 border-t">
              <Label className="text-sm font-medium flex items-center gap-1.5 mb-2"><Paperclip className="h-3.5 w-3.5" /> Document Attachment</Label>
              {credit?.attachment_url ? (
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2">
                  <FileDown className="h-4 w-4 text-blue-600 shrink-0" />
                  <button onClick={handleAttachmentDownload} className="text-sm text-blue-600 hover:underline truncate text-left" data-testid="credit-attachment-link">{credit.attachment_filename || 'Download attachment'}</button>
                  {!isPosted && <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto shrink-0" onClick={handleAttachmentRemove} data-testid="remove-credit-attachment-btn"><X className="h-3.5 w-3.5 text-destructive" /></Button>}
                </div>
              ) : !isPosted ? (
                <div className="relative">
                  <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleAttachmentUpload} disabled={uploading} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" data-testid="credit-attachment-input" />
                  <Button variant="outline" size="sm" disabled={uploading} className="pointer-events-none">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    {uploading ? 'Uploading...' : 'Attach credit note / document'}
                  </Button>
                </div>
              ) : <p className="text-xs text-muted-foreground">No attachment</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Line Items</CardTitle>
            {!isPosted && <Button variant="outline" size="sm" onClick={addLine} data-testid="add-credit-line-btn"><Plus className="h-4 w-4 mr-1" /> Add Line</Button>}
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead className="min-w-[280px]">Product</TableHead>
                  <TableHead className="w-[100px]">Qty</TableHead>
                  <TableHead className="w-[140px]">Purchase Unit</TableHead>
                  <TableHead className="w-[120px]">Cost ({currencySymbol}) <span className="text-[10px] font-normal text-muted-foreground block">{vatLabel}</span></TableHead>
                  <TableHead className="w-[120px] text-right">Line Total</TableHead>
                  {!isPosted && <TableHead className="w-[50px]" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((ln, idx) => {
                  const qty = parseFloat(ln.quantity) || 0;
                  const cost = parseFloat(ln.cost_price) || 0;
                  const lineTotal = qty * cost;
                  const unitOptions = getUnitOptions(ln.beverage_product_id);
                  const conversionNote = getConversionNote(ln);
                  const yieldHint = getYieldHint(getProduct(ln.beverage_product_id));
                  return (
                    <TableRow key={idx} data-testid={`credit-line-row-${idx}`}>
                      <TableCell className="text-muted-foreground text-sm">{idx + 1}</TableCell>
                      <TableCell>
                        {editDisabled ? <span className="text-sm">{getProductName(ln.beverage_product_id) || ln.product_name || '-'}</span> : (
                          <ProductPicker products={availableProducts} value={ln.beverage_product_id} onChange={(val) => handleProductChange(idx, val)} disabled={editDisabled} placeholder="Search product..." testId={`credit-line-product-${idx}`} />
                        )}
                        {yieldHint && (
                          <p className="text-[10px] text-purple-600 mt-0.5 font-medium" data-testid={`credit-line-yield-${idx}`}>{yieldHint}</p>
                        )}
                      </TableCell>
                      <TableCell><Input type="number" step="0.01" className="h-9 text-sm" value={ln.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} disabled={editDisabled} data-testid={`credit-line-qty-${idx}`} /></TableCell>
                      <TableCell>
                        {isPosted ? <span className="text-sm capitalize">{ln.unit_label || '-'}</span> : (
                          <div>
                            {unitOptions.length > 0 ? (
                              <Select value={ln.unit_label || 'none'} onValueChange={(v) => updateLine(idx, 'unit_label', v === 'none' ? '' : v)}>
                                <SelectTrigger className="h-9 text-sm" data-testid={`credit-line-unit-${idx}`}><SelectValue placeholder="Unit" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">--</SelectItem>
                                  {unitOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input placeholder="Unit" value={ln.unit_label} onChange={(e) => updateLine(idx, 'unit_label', e.target.value)} className="h-9 text-sm" data-testid={`credit-line-unit-${idx}`} />
                            )}
                            {conversionNote && <p className="text-[10px] text-blue-600 mt-0.5 font-medium">{conversionNote}</p>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell><Input type="number" step="0.01" className="h-9 text-sm" value={ln.cost_price} onChange={(e) => updateLine(idx, 'cost_price', e.target.value)} disabled={editDisabled} data-testid={`credit-line-cost-${idx}`} /></TableCell>
                      <TableCell className="text-right font-mono text-sm">{currencySymbol}{lineTotal.toFixed(2)}</TableCell>
                      {!isPosted && (
                        <TableCell><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeLine(idx)} disabled={lines.length <= 1} data-testid={`remove-credit-line-${idx}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex justify-end">
            <div className="w-full max-w-xs space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Lines Total <span className="text-xs">({vatLabel})</span></span>
                <span className="font-mono font-medium">{currencySymbol}{linesTotal.toFixed(2)}</span>
              </div>
              {vatRate > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">VAT ({settings.default_vat_rate}%)</span>
                    <span className="font-mono text-muted-foreground">{currencySymbol}{vatBreakdown.vat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{invoiceBasis === 'gross' ? 'Net' : 'Gross'} Total</span>
                    <span className="font-mono text-muted-foreground">{currencySymbol}{invoiceBasis === 'gross' ? vatBreakdown.net.toFixed(2) : vatBreakdown.gross.toFixed(2)}</span>
                  </div>
                </>
              )}
              {header.credit_total && (
                <>
                  <Separator />
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Credit Total</span>
                    <span className="font-mono font-medium">{currencySymbol}{creditTotal.toFixed(2)}</span>
                  </div>
                  <div className={`flex justify-between text-sm font-medium ${Math.abs(variance) > 0.01 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    <span className="flex items-center gap-1">{Math.abs(variance) > 0.01 && <AlertTriangle className="h-3.5 w-3.5" />}Variance</span>
                    <span className="font-mono">{variance >= 0 ? '+' : ''}{currencySymbol}{variance.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
